/**
 * FERP Portal - 5×5 Primitive Grid Component
 *
 * Visual representation of the 25-primitive system organized by lifecycle phase.
 * This is the core visualization for the Architect view layer.
 *
 * CONSTRAINTS:
 * - 5 phases × 5 primitives = 25 total
 * - Each primitive shows count, health, and navigation
 * - Life-critical primitives highlighted
 * - Real-time updates via subscriptions
 */

import { useMemo } from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  SimpleGrid,
  ThemeIcon,
  Tooltip,
  Progress,
  Card,
  Loader,
  Alert,
  Box,
  RingProgress,
} from '@mantine/core';
import {
  IconCircuitDiode,
  IconSettings,
  IconPlayerPlay,
  IconChartBar,
  IconShield,
  IconAlertTriangle,
  IconCheck,
  IconX,
  IconClock,
  // DESIGN phase icons
  IconSitemap,
  IconBox,
  IconArrowsSort,
  IconFileText,
  IconShieldLock,
  // CONFIGURE phase icons
  IconUsers,
  IconUser,
  IconCheckbox,
  IconContract,
  IconLock,
  // EXECUTE phase icons
  IconCircle,
  IconListCheck,
  IconCircleCheck,
  IconFileDescription,
  IconList,
  // MONITOR phase icons
  IconBolt,
  IconMail,
  IconRoute,
  IconFlag,
  IconReceipt,
  // OPTIMIZE phase icons
  IconClipboardCheck,
  IconChartDots,
  IconCalendar,
  IconDatabase,
  IconPlug,
} from '@tabler/icons-react';
import { useTenantId } from '@/auth';
import {
  useListWorkflowsQuery,
  useListTasksQuery,
  GqlTaskStatus,
  // NOTE: Most primitive GraphQL queries are implemented in backend but not yet exposed in this UI
  // The grid shows workflow and task data, other primitives show placeholder stats
  // TODO: Add GraphQL queries for all 25 primitives when UI needs real-time stats
} from '@/generated/graphql';
import {
  SafetyLevel,
  SAFETY_LEVEL_UI_CONFIG,
  getLifeCriticalBorderStyle,
} from '@/config/safety-levels';

// ============================================================================
// TYPES
// ============================================================================

export type LifecyclePhase = 'DESIGN' | 'CONFIGURE' | 'EXECUTE' | 'MONITOR' | 'OPTIMIZE';

export interface PrimitiveDefinition {
  id: string;
  name: string;
  phase: LifecyclePhase;
  icon: typeof IconCircuitDiode;
  description: string;
  lifeCritical: boolean;
  color: string;
}

export interface PrimitiveStats {
  total: number;
  active: number;
  lifeCritical: number;
  health: 'healthy' | 'degraded' | 'critical';
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PHASE_CONFIG: Record<LifecyclePhase, { label: string; color: string; icon: typeof IconCircuitDiode }> = {
  DESIGN: { label: 'Design', color: 'blue', icon: IconCircuitDiode },
  CONFIGURE: { label: 'Configure', color: 'grape', icon: IconSettings },
  EXECUTE: { label: 'Execute', color: 'cyan', icon: IconPlayerPlay },
  MONITOR: { label: 'Monitor', color: 'orange', icon: IconChartBar },
  OPTIMIZE: { label: 'Optimize', color: 'red', icon: IconShield },
};

const PRIMITIVES: PrimitiveDefinition[] = [
  // DESIGN Phase
  { id: 'workflow', name: 'Workflow', phase: 'DESIGN', icon: IconSitemap, description: 'Process templates and definitions', lifeCritical: true, color: 'blue' },
  { id: 'template', name: 'Template', phase: 'DESIGN', icon: IconBox, description: 'Reusable workflow templates', lifeCritical: false, color: 'blue' },
  { id: 'dependency', name: 'Dependency', phase: 'DESIGN', icon: IconArrowsSort, description: 'Workflow ordering and prerequisites', lifeCritical: false, color: 'blue' },
  { id: 'policy', name: 'Policy', phase: 'DESIGN', icon: IconFileText, description: 'Zen Engine policy rules', lifeCritical: true, color: 'blue' },
  { id: 'role', name: 'Role', phase: 'DESIGN', icon: IconShieldLock, description: 'RBAC role definitions', lifeCritical: true, color: 'blue' },

  // CONFIGURE Phase
  { id: 'team', name: 'Team', phase: 'CONFIGURE', icon: IconUsers, description: 'Team structures and hierarchies', lifeCritical: false, color: 'grape' },
  { id: 'actor', name: 'Actor', phase: 'CONFIGURE', icon: IconUser, description: 'User and system identities', lifeCritical: true, color: 'grape' },
  { id: 'validation', name: 'Validation', phase: 'CONFIGURE', icon: IconCheckbox, description: 'Validation rules and schemas', lifeCritical: true, color: 'grape' },
  { id: 'contract', name: 'Contract', phase: 'CONFIGURE', icon: IconContract, description: 'SLA definitions and agreements', lifeCritical: true, color: 'grape' },
  { id: 'constraint', name: 'Constraint', phase: 'CONFIGURE', icon: IconLock, description: 'Business rules and boundaries', lifeCritical: true, color: 'grape' },

  // EXECUTE Phase
  { id: 'instance', name: 'Instance', phase: 'EXECUTE', icon: IconCircle, description: 'Workflow execution instances', lifeCritical: true, color: 'cyan' },
  { id: 'task', name: 'Task', phase: 'EXECUTE', icon: IconListCheck, description: 'Work items and assignments', lifeCritical: true, color: 'cyan' },
  { id: 'approval', name: 'Approval', phase: 'EXECUTE', icon: IconCircleCheck, description: 'Multi-stage approval workflows', lifeCritical: true, color: 'cyan' },
  { id: 'document', name: 'Document', phase: 'EXECUTE', icon: IconFileDescription, description: 'Document storage and versioning', lifeCritical: false, color: 'cyan' },
  { id: 'queue', name: 'Queue', phase: 'EXECUTE', icon: IconList, description: 'Task routing and assignment', lifeCritical: false, color: 'cyan' },

  // MONITOR Phase
  { id: 'event', name: 'Event', phase: 'MONITOR', icon: IconBolt, description: 'System events and notifications', lifeCritical: false, color: 'orange' },
  { id: 'notification', name: 'Notification', phase: 'MONITOR', icon: IconMail, description: 'User notifications', lifeCritical: false, color: 'orange' },
  { id: 'trace', name: 'Trace', phase: 'MONITOR', icon: IconRoute, description: 'Distributed tracing and observability', lifeCritical: false, color: 'orange' },
  { id: 'checkpoint', name: 'Checkpoint', phase: 'MONITOR', icon: IconFlag, description: 'Workflow state checkpoints', lifeCritical: true, color: 'orange' },
  { id: 'transaction', name: 'Transaction', phase: 'MONITOR', icon: IconReceipt, description: 'Saga transactions and compensation', lifeCritical: true, color: 'orange' },

  // OPTIMIZE Phase
  { id: 'audit', name: 'Audit', phase: 'OPTIMIZE', icon: IconClipboardCheck, description: 'Immutable audit trail', lifeCritical: true, color: 'red' },
  { id: 'analytics', name: 'Analytics', phase: 'OPTIMIZE', icon: IconChartDots, description: 'Performance analytics and insights', lifeCritical: false, color: 'red' },
  { id: 'schedule', name: 'Schedule', phase: 'OPTIMIZE', icon: IconCalendar, description: 'Time-based triggers and schedules', lifeCritical: false, color: 'red' },
  { id: 'resource', name: 'Resource', phase: 'OPTIMIZE', icon: IconDatabase, description: 'Resource allocation and management', lifeCritical: false, color: 'red' },
  { id: 'integration', name: 'Integration', phase: 'OPTIMIZE', icon: IconPlug, description: 'External system integrations', lifeCritical: false, color: 'red' },
];

const PHASES: LifecyclePhase[] = ['DESIGN', 'CONFIGURE', 'EXECUTE', 'MONITOR', 'OPTIMIZE'];

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface PrimitiveCardProps {
  primitive: PrimitiveDefinition;
  stats: PrimitiveStats;
  onClick?: () => void;
}

function PrimitiveCard({ primitive, stats, onClick }: PrimitiveCardProps) {
  const PrimitiveIcon = primitive.icon;

  const healthColor = {
    healthy: 'green',
    degraded: 'yellow',
    critical: 'red',
  }[stats.health];

  const healthIcon = {
    healthy: IconCheck,
    degraded: IconClock,
    critical: IconX,
  }[stats.health];

  const HealthIcon = healthIcon;

  return (
    <Card
      withBorder
      padding="sm"
      style={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.1s, box-shadow 0.1s',
        ...(primitive.lifeCritical ? getLifeCriticalBorderStyle(SafetyLevel.LIFE_CRITICAL) : {}),
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        if (onClick) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = 'var(--mantine-shadow-md)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <Group justify="space-between" mb="xs">
        <Group gap="xs">
          <ThemeIcon size="sm" color={primitive.color} variant="light">
            <PrimitiveIcon size={14} />
          </ThemeIcon>
          <Text size="sm" fw={600}>{primitive.name}</Text>
        </Group>
        <Tooltip label={`Health: ${stats.health}`}>
          <ThemeIcon size="xs" color={healthColor} variant="light" radius="xl">
            <HealthIcon size={10} />
          </ThemeIcon>
        </Tooltip>
      </Group>

      <Text size="xs" c="dimmed" mb="xs" lineClamp={1}>
        {primitive.description}
      </Text>

      <Group justify="space-between" ta="center">
        <Group gap={4}>
          <Text size="lg" fw={700}>{stats.total}</Text>
          <Text size="xs" c="dimmed">total</Text>
        </Group>

        {stats.lifeCritical > 0 && (
          <Tooltip label={`${stats.lifeCritical} life-critical`}>
            <Badge size="xs" color="red" variant="light">
              <Group gap={2}>
                <IconAlertTriangle size={10} />
                {stats.lifeCritical}
              </Group>
            </Badge>
          </Tooltip>
        )}
      </Group>

      {stats.active > 0 && (
        <Progress
          value={(stats.active / Math.max(stats.total, 1)) * 100}
          size="xs"
          mt="xs"
          color={primitive.color}
        />
      )}
    </Card>
  );
}

interface PhaseHeaderProps {
  phase: LifecyclePhase;
  primitiveCount: number;
  lifeCriticalCount: number;
}

function PhaseHeader({ phase, primitiveCount, lifeCriticalCount }: PhaseHeaderProps) {
  const config = PHASE_CONFIG[phase];
  const PhaseIcon = config.icon;

  return (
    <Group justify="space-between" mb="xs">
      <Group gap="xs">
        <ThemeIcon size="md" color={config.color} variant="filled">
          <PhaseIcon size={16} />
        </ThemeIcon>
        <Text size="sm" fw={600} tt="uppercase">{config.label}</Text>
      </Group>
      <Group gap="xs">
        <Badge size="xs" color={config.color} variant="light">
          {primitiveCount} primitives
        </Badge>
        {lifeCriticalCount > 0 && (
          <Badge size="xs" color="red" variant="light">
            {lifeCriticalCount} life-critical
          </Badge>
        )}
      </Group>
    </Group>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PrimitiveGridProps {
  onPrimitiveClick?: (primitive: PrimitiveDefinition) => void;
  compact?: boolean;
}

export function PrimitiveGrid({ onPrimitiveClick, compact = false }: PrimitiveGridProps) {
  const tenantId = useTenantId();

  // Fetch counts for each primitive type
  // Only workflows and tasks have GraphQL queries implemented
  const { data: workflowsData, loading: workflowsLoading } = useListWorkflowsQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  const { data: tasksData, loading: tasksLoading } = useListTasksQuery({
    variables: { limit: 100 },
    skip: !tenantId,
  });

  const loading = workflowsLoading || tasksLoading;

  // Compute stats for each primitive
  const primitiveStats = useMemo(() => {
    const stats: Record<string, PrimitiveStats> = {};

    // Workflow stats (has GraphQL query)
    const workflows = workflowsData?.listWorkflows?.items ?? [];
    stats.workflow = {
      total: workflows.length,
      active: workflows.filter((w) => w.active).length,
      lifeCritical: 0, // Workflow type doesn't have safetyLevel field
      health: workflows.length > 0 ? 'healthy' : 'degraded',
    };

    // Task stats (has GraphQL query)
    const tasks = tasksData?.taskApiListTasks?.items ?? [];
    stats.task = {
      total: tasks.length,
      active: tasks.filter((t) => t.status === GqlTaskStatus.IN_PROGRESS).length,
      lifeCritical: 0, // Task type doesn't have safetyLevel field in current schema
      health: tasks.filter((t) => t.status === GqlTaskStatus.FAILED).length > 0 ? 'critical' : 'healthy',
    };

    // Default stats for primitives without UI queries yet
    // All 25 primitive types have backend implementations, but most don't expose real-time stats in this UI
    const defaultStats: PrimitiveStats = {
      total: 0,
      active: 0,
      lifeCritical: 0,
      health: 'degraded',
    };

    PRIMITIVES.forEach(p => {
      if (!stats[p.id]) {
        stats[p.id] = defaultStats;
      }
    });

    return stats;
  }, [workflowsData, tasksData]);

  // Compute phase summaries
  const phaseSummaries = useMemo(() => {
    const summaries: Record<LifecyclePhase, { total: number; lifeCritical: number; health: 'healthy' | 'degraded' | 'critical' }> = {
      DESIGN: { total: 0, lifeCritical: 0, health: 'healthy' },
      CONFIGURE: { total: 0, lifeCritical: 0, health: 'healthy' },
      EXECUTE: { total: 0, lifeCritical: 0, health: 'healthy' },
      MONITOR: { total: 0, lifeCritical: 0, health: 'healthy' },
      OPTIMIZE: { total: 0, lifeCritical: 0, health: 'healthy' },
    };

    PRIMITIVES.forEach(p => {
      const stats = primitiveStats[p.id];
      if (stats) {
        summaries[p.phase].total += stats.total;
        summaries[p.phase].lifeCritical += stats.lifeCritical;
        if (stats.health === 'critical') {
          summaries[p.phase].health = 'critical';
        } else if (stats.health === 'degraded' && summaries[p.phase].health !== 'critical') {
          summaries[p.phase].health = 'degraded';
        }
      }
    });

    return summaries;
  }, [primitiveStats]);

  // Overall health
  const overallHealth = useMemo(() => {
    const hasCritical = Object.values(phaseSummaries).some(s => s.health === 'critical');
    const hasDegraded = Object.values(phaseSummaries).some(s => s.health === 'degraded');
    return hasCritical ? 'critical' : hasDegraded ? 'degraded' : 'healthy';
  }, [phaseSummaries]);

  const totalLifeCritical = useMemo(() => {
    return Object.values(primitiveStats).reduce((sum, s) => sum + s.lifeCritical, 0);
  }, [primitiveStats]);

  if (loading) {
    return (
      <Paper p="xl" withBorder ta="center">
        <Loader size="lg" />
        <Text c="dimmed" mt="md">Loading primitive grid...</Text>
      </Paper>
    );
  }

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <div>
          <Title order={3}>25-Primitive System</Title>
          <Text size="sm" c="dimmed">5 phases × 5 primitives per phase</Text>
        </div>
        <Group gap="md">
          <RingProgress
            size={60}
            thickness={6}
            sections={PHASES.map((phase, i) => ({
              value: 20,
              color: PHASE_CONFIG[phase].color,
            }))}
            label={
              <Text ta="center" size="xs" fw={700}>
                25
              </Text>
            }
          />
          {totalLifeCritical > 0 && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />} p="xs">
              <Text size="xs">{totalLifeCritical} life-critical items</Text>
            </Alert>
          )}
          <Tooltip label={`System health: ${overallHealth}`}>
            <ThemeIcon
              size="lg"
              color={overallHealth === 'healthy' ? 'green' : overallHealth === 'degraded' ? 'yellow' : 'red'}
              variant="light"
              radius="xl"
            >
              {overallHealth === 'healthy' ? <IconCheck size={20} /> :
               overallHealth === 'degraded' ? <IconClock size={20} /> : <IconX size={20} />}
            </ThemeIcon>
          </Tooltip>
        </Group>
      </Group>

      {/* Phase Grid */}
      {PHASES.map(phase => {
        const phasePrimitives = PRIMITIVES.filter(p => p.phase === phase);
        const summary = phaseSummaries[phase];

        return (
          <Paper key={phase} p="md" withBorder>
            <PhaseHeader
              phase={phase}
              primitiveCount={5}
              lifeCriticalCount={phasePrimitives.filter(p => p.lifeCritical).length}
            />
            <SimpleGrid cols={compact ? 3 : 5} spacing="sm">
              {phasePrimitives.map(primitive => (
                <PrimitiveCard
                  key={primitive.id}
                  primitive={primitive}
                  stats={primitiveStats[primitive.id] ?? { total: 0, active: 0, lifeCritical: 0, health: 'degraded' }}
                  onClick={onPrimitiveClick ? () => onPrimitiveClick(primitive) : undefined}
                />
              ))}
            </SimpleGrid>
          </Paper>
        );
      })}

      {/* Legend */}
      <Paper p="sm" withBorder bg="gray.0">
        <Group justify="center" gap="xl">
          <Group gap="xs">
            <Box w={12} h={12} style={{ borderLeft: '3px solid var(--mantine-color-red-6)' }} />
            <Text size="xs" c="dimmed">Life-Critical Primitive</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="xs" color="green" variant="light" radius="xl">
              <IconCheck size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Healthy</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="xs" color="yellow" variant="light" radius="xl">
              <IconClock size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Degraded</Text>
          </Group>
          <Group gap="xs">
            <ThemeIcon size="xs" color="red" variant="light" radius="xl">
              <IconX size={8} />
            </ThemeIcon>
            <Text size="xs" c="dimmed">Critical</Text>
          </Group>
        </Group>
      </Paper>
    </Stack>
  );
}

export default PrimitiveGrid;
